import find from 'lodash/find.js';

import { initialState } from '@/Helpers.js';
import UserGroup from '@/models/UserGroup.js';

export default class GateKeeper {
    forUser(user) {
        this.user = user;

        return this;
    }

    before(user) {
        if (user.hasAnyRole(['Owner', 'Admin', 'Instruction Admin'])) {
            return true;
        }

        return null;
    }

    async userHasAccessToCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.enrollments.length > 0) {
            const courseEnrollment = find(this.user.enrollments, {
                course_id: Number.parseInt(courseId, 10),
            });

            const disabledStatuses = [
                ...initialState('AccountStatuses.disabling'),
                ...initialState('AccountStatuses.removed'),
            ];

            const hasDisabledStatus = courseEnrollment?.statuses.some(status => disabledStatuses.includes(status.name));

            if (courseEnrollment && !hasDisabledStatus) {
                return true;
            }
        }

        const userGroups = UserGroup.query().with(['role']).where('user_id', this.user.id).get();

        // Use Promise.all to check all user groups in parallel
        const accessChecks = await Promise.all(
            userGroups.map(async userGroup => {
                // Check role first
                const isNotStudent = !['Student', 'Teaching Assistant'].includes(userGroup.role?.name);

                if (!isNotStudent) {
                    return false;
                }

                // Check if group has course access using the new async method
                const hasCourseAccess = await userGroup.hasCourseAsync(Number.parseInt(courseId, 10));

                return hasCourseAccess;
            })
        );

        const hasAccess = accessChecks.some(result => result);

        return hasAccess;
    }

    userHasAccessToPreviewQuiz(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.isStudentOf(courseId)) {
            return false;
        }

        if (this.user.hasEnrolledPermissionTo('view all quizzes') && this.user.isInstructorOf(courseId)) {
            return true;
        }

        if (this.user.hasRoleWithinCourseGroup(courseId, 'Instructor')) {
            return this.user.hasGroupPermissionWithinCourse(courseId, 'view all quizzes');
        }

        return false;
    }

    userHasAccessToReport(report) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.hasPermissionTo('view reports') && this.user.hasPermissionTo(`view ${report}`)) {
            return true;
        }

        if (this.user.hasGroupPermissionTo('view reports') && this.user.hasGroupPermissionTo(`view ${report}`)) {
            return true;
        }

        if (this.user.hasEnrolledPermissionTo('view reports') && this.user.hasEnrolledPermissionTo(`view ${report}`)) {
            return true;
        }

        return false;
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param courseId
     * @returns {boolean|*}
     */
    userHasAccessToEditCourse(courseId) {
        if (this.before(this.user)) {
            return true;
        }

        if (this.user.hasRoleWithinCourseGroup(courseId, 'Content Editor')) {
            return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
        }

        if (this.user.hasPermissionTo('edit a course')) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(courseId, 'edit a course');
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasPermissionTo(permission);
    }

    /**
     * Check if the user has access to edit the course.
     * @note This method cannot check properties on the actual course because it is not loaded yet.
     * @param permission
     * @returns {boolean|*}
     */
    userHasGroupPermission(permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionTo(permission);
    }

    userHasGroupPermissionWithinCourse(courseId, permission) {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.hasGroupPermissionWithinCourse(Number(courseId), permission);
    }

    userIsGroupInstructor() {
        if (this.before(this.user)) {
            return true;
        }

        return this.user.isGroupInstructor();
    }

    userIsViewingOwnResource(resourceUserId) {
        return this.user.id === resourceUserId;
    }

    denialMessage(to) {
        return `Access denied to ${to.path}`;
    }
}
